/**
 * TitleBar — platform-aware app header / window chrome.
 *
 * On Desktop (Tauri): Renders custom window chrome with minimize/maximize/close
 * buttons and listens for window-maximized/unmaximized events.
 *
 * On Web / Mobile: Renders a simplified app header (no window controls).
 *
 * Always provides:
 * - App logo / name
 * - Optional network-status indicator (online/offline badge + toast)
 */
import { useState, useEffect } from "react";
import { VscChromeMinimize, VscChromeMaximize, VscChromeClose } from "react-icons/vsc";
import { useSelector } from "react-redux";
import { platform, isDesktop, isWeb, isMobile } from "@platform";
import { store } from "@store/index";
import { exitAppReducer } from "@store/ipc-events-modal-splice";
import appIcon from "../../app/assets/images/logo.png";
import { heartBeat } from "@service/heart-beat-handler";
import toast, { Toaster } from "react-hot-toast";
import { getColorTheme } from "@service/local-storage-handler";
import ConnectionStatus from "@components/connection-status/ConnectionStatus";
import "./TitleBar.scss";

interface TitleBarProps {
  showNetworkStatus?: boolean;
  minimal?: boolean;
}

const RestoreDownIcon = () => (
  <svg
    className="window-control-icon restore-icon"
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <rect x="5" y="3" width="8" height="8" stroke="currentColor" strokeWidth="1.6" />
    <rect x="3" y="5" width="8" height="8" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const TitleBar = ({ showNetworkStatus = false, minimal = false }: TitleBarProps) => {
  const themeFromStore = useSelector(({ theme }) => theme.theme);
  // Default to dark if theme is not set in Redux (fallback to localStorage)
  const colorTheme = themeFromStore || getColorTheme() || "dark";
  const [isOnline, setIsOnline] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);

  const syncDesktopWindowState = async () => {
    if (!isDesktop) return;
    try {
      const { getCurrentWindow } = await import("@tauri-apps/api/window");
      const maximized = await getCurrentWindow().isMaximized();
      setIsMaximized(maximized);
    } catch {
      // Ignore non-desktop/bridge timing errors.
    }
  };

  useEffect(() => {
    if (!showNetworkStatus) return;

    if (isOnline) {
      // Socket is managed by WithNav — nothing to do here.
    } else {
      toast.error(
        (t) => (
          <div className={`toast-msgs-container ${colorTheme}`}>
            <div>
              <label
                htmlFor="toast-msg"
                style={{ color: colorTheme === "dark" ? "#f7fafc" : "#1a202c" }}
              >
                Network Disconnected
              </label>
            </div>
            <div>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="dismiss-btn"
              >
                Dismiss
              </button>
            </div>
          </div>
        ),
        {
          duration: 6000,
          style: {
            fontSize: "0.8rem",
            borderRadius: 0,
            textAlign: "left",
            paddingRight: 0,
          },
        }
      );
      // Socket teardown is handled by WithNav on unmount.
    }
  }, [isOnline, showNetworkStatus]);

  useEffect(() => {
    if (!showNetworkStatus) return;

    // Check once immediately on mount, then every 30 seconds.
    // 500 ms was firing 2 HTTP requests/second for every logged-in user.
    const check = async () => {
      try {
        await heartBeat();
        setIsOnline(true);
      } catch {
        setIsOnline(false);
      }
    };
    check();
    const interval = setInterval(check, 30_000);
    return () => clearInterval(interval);
  }, [showNetworkStatus]);

  // Desktop-only: listen for Tauri window maximize/unmaximize events
  useEffect(() => {
    if (!isDesktop) return;

    let unlistenMax: (() => void) | null = null;
    let unlistenUnmax: (() => void) | null = null;

    const setup = async () => {
      try {
        const { listen } = await import("@tauri-apps/api/event");
        unlistenMax = await listen("window-maximized", () => setIsMaximized(true));
        unlistenUnmax = await listen("window-unmaximized", () => setIsMaximized(false));
        await syncDesktopWindowState();
      } catch {
        // Tauri not available
      }
    };
    setup();

    return () => {
      unlistenMax?.();
      unlistenUnmax?.();
    };
  }, []);

  // Some Windows/Tauri boots report maximize state a bit later than initial mount.
  // Retry a few times so the icon is correct immediately after app launch.
  useEffect(() => {
    if (!isDesktop) return;

    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const run = async () => {
      if (cancelled) return;
      await syncDesktopWindowState();
      attempt += 1;
      if (attempt < 8) {
        timer = setTimeout(run, 250);
      }
    };

    run();

    const onFocus = () => {
      syncDesktopWindowState().catch(() => {
        // ignore
      });
    };
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const handleMinimize = () => {
    platform.minimizeWindow().catch(console.error);
  };

  const handleMaximize = async () => {
    try {
      await platform.maximizeWindow();
      await syncDesktopWindowState();
    } catch (e) {
      console.error(e);
    }
  };

  const handleClose = () => {
    if (isDesktop) {
      store.dispatch(exitAppReducer({ exitApp: true }));
      return;
    }
    platform.closeWindow().catch(console.error);
  };

  const handleControlMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  // On web, no title bar needed - only render toaster
  if (isWeb) {
    return (
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background:
              colorTheme === "dark"
                ? "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)"
                : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            color: colorTheme === "dark" ? "#f7fafc" : "#1a202c",
            borderRadius: "12px",
            border:
              colorTheme === "dark" ? "1px solid #333333" : "1px solid #e2e8f0",
            padding: "16px 20px",
            boxShadow:
              colorTheme === "dark"
                ? "0 20px 40px rgba(0, 0, 0, 0.5)"
                : "0 20px 40px rgba(0, 0, 0, 0.15)",
            fontSize: "14px",
            fontWeight: "500",
          },
        }}
      />
    );
  }

  // On mobile, skip the visible title bar (same as web) — keep hooks + Toaster
  if (isMobile) {
    return (
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background:
                colorTheme === "dark"
                  ? "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)"
                  : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              color: colorTheme === "dark" ? "#f7fafc" : "#1a202c",
              borderRadius: "12px",
              border:
                colorTheme === "dark" ? "1px solid #333333" : "1px solid #e2e8f0",
              padding: "12px 16px",
              boxShadow:
                colorTheme === "dark"
                  ? "0 10px 20px rgba(0, 0, 0, 0.5)"
                  : "0 10px 20px rgba(0, 0, 0, 0.15)",
              fontSize: "13px",
              fontWeight: "500",
            },
          }}
        />
    );
  }

  return (
    <div className={`title-bar ${colorTheme}${minimal ? ' title-bar--minimal' : ''}`}>
      <div className="title-bar-drag-region" onDoubleClick={handleMaximize}>
        {!minimal && (
          <div className="title-bar-left">
            <div className="app-branding">
              <img src={appIcon} alt="MarketView" className="app-icon" />
              <span className="app-title">MarketView</span>
            </div>
          </div>
        )}
        {minimal && <div className="title-bar-left" />}

        <div className="title-bar-right">
          {showNetworkStatus && (
            <ConnectionStatus />
          )}
          {/* Window controls are only rendered on Desktop (custom title bar) */}
          {platform.supportsCustomTitleBar && (
            <>
              <button
                className="window-control minimize"
                onMouseDown={handleControlMouseDown}
                onClick={handleMinimize}
                title="Minimize"
              >
                <VscChromeMinimize size={14} />
              </button>
              <button
                className="window-control maximize"
                onMouseDown={handleControlMouseDown}
                onClick={handleMaximize}
                title={isMaximized ? "Restore Down" : "Maximize"}
              >
                {isMaximized ? <RestoreDownIcon /> : <VscChromeMaximize size={14} />}
              </button>
              <button
                className="window-control close"
                onMouseDown={handleControlMouseDown}
                onClick={handleClose}
                title="Close"
              >
                <VscChromeClose size={14} />
              </button>
            </>
          )}
        </div>
      </div>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background:
              colorTheme === "dark"
                ? "linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)"
                : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            color: colorTheme === "dark" ? "#f7fafc" : "#1a202c",
            borderRadius: "12px",
            border:
              colorTheme === "dark" ? "1px solid #333333" : "1px solid #e2e8f0",
            padding: "16px 20px",
            boxShadow:
              colorTheme === "dark"
                ? "0 20px 40px rgba(0, 0, 0, 0.5)"
                : "0 20px 40px rgba(0, 0, 0, 0.15)",
            fontSize: "14px",
            fontWeight: "500",
          },
          success: {
            style: {
              borderLeft: "4px solid #22c55e",
            },
            iconTheme: {
              primary: "#22c55e",
              secondary: colorTheme === "dark" ? "#1e1e1e" : "#ffffff",
            },
          },
          error: {
            style: {
              borderLeft: "4px solid #ef4444",
            },
            iconTheme: {
              primary: "#ef4444",
              secondary: colorTheme === "dark" ? "#1e1e1e" : "#ffffff",
            },
          },
        }}
      />
    </div>
  );
};

export default TitleBar;
